/**
 * WidgetCard + WidgetStateView — shared shell + state renderer that every
 * module-owned widget uses, so the chrome (title bar, drilldown chevron,
 * state badge, action menu) stays consistent across the long tail of widgets.
 */

import * as React from 'react';
import { ChevronRight, MoreHorizontal, RefreshCw, WifiOff, Clock } from 'lucide-react';
import { Card, CardContent } from '../primitives/card';
import { Badge } from '../primitives/badge';
import { Button } from '../primitives/button';
import { toStaleDate, formatStaleDelta, type StaleSince } from '../domain/live/stale-since';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../primitives/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../primitives/tooltip';
import { cn } from '../lib/utils';
import { LoadingSpinner } from '../domain/loading-spinner';
import { ViewState } from '../domain/view-state';
import { EvidenceExportButton } from '../domain/evidence-export';
import type { EvidenceSection } from '../domain/evidence-export';
import type {
  WidgetDescriptor,
  WidgetDataState,
  WidgetDataSourceFlavor,
} from './widget-contract';
import { resolveWidgetCardMenu, type WidgetCardAction } from './widget-card-menu';

export { resolveWidgetCardMenu };
export type { WidgetCardAction };

// ────────────────────────────────────────────────────────────────────────
// State badge — communicates which source the widget is currently using.
// ────────────────────────────────────────────────────────────────────────

interface StateBadgeProps {
  source: WidgetDataSourceFlavor;
}

const SOURCE_TONE: Record<WidgetDataSourceFlavor, { label: string; className: string }> = {
  live: { label: 'Live', className: 'bg-[hsl(var(--state-running))]/15 text-[hsl(var(--state-running))] border-[hsl(var(--state-running))]/30' },
  cached: { label: 'Cached', className: 'bg-[hsl(var(--alarm-minor))]/15 text-[hsl(var(--alarm-minor))] border-[hsl(var(--alarm-minor))]/30' },
  simulator: { label: 'Simulator', className: 'bg-brand-primary/15 text-brand-primary border-brand-primary/30' },
  stale: { label: 'Stale', className: 'bg-muted text-muted-foreground border-border' },
};

export const WidgetSourceBadge: React.FC<StateBadgeProps> = ({ source }) => {
  const tone = SOURCE_TONE[source];
  return (
    <span
      data-testid={`widget-source-${source}`}
      className={cn('rounded border px-1.5 py-0.5 text-[10px] font-medium', tone.className)}
    >
      {tone.label}
    </span>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Stale / offline badge — distinct from the source badge. The source badge
// answers "where did this data come from"; this answers "is what you're
// looking at current". Offline (lost the live connection) and stale (the
// connection is fine but the data has aged past its freshness window) are
// surfaced differently so an operator can tell a dropped feed from a slow one.
// ────────────────────────────────────────────────────────────────────────

/**
 * How a widget reacts once its data is considered stale.
 *   - `'badge'` (default): keep showing the last-known body and surface a
 *     "Stale (Nm ago)" badge with a live, 1Hz-updating age delta.
 *   - `'show-empty'`: treat stale data as no data — the card hides its body and
 *     shows the empty state (for safety-relevant tiles where a stale value is
 *     worse than none).
 */
export type WidgetStaleBehavior = 'badge' | 'show-empty';

/** Compute the elapsed age label from a staleSince timestamp at `now`. */
function staleAgeLabel(since: Date, now: number): string {
  return formatStaleDelta(now - since.getTime());
}

interface StaleBadgeProps {
  /** Last-fresh timestamp. The badge shows the elapsed delta since this. */
  staleSince: Date;
  /** True when the underlying live connection is down (vs. merely aged data). */
  offline?: boolean;
}

const StaleBadge: React.FC<StaleBadgeProps> = ({ staleSince, offline }) => {
  // 1Hz age timer so the "(Nm ago)" delta stays current without the consuming
  // widget re-rendering. Cleared on unmount / prop change.
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const age = staleAgeLabel(staleSince, now);
  const Icon = offline ? WifiOff : Clock;
  const label = offline ? `Offline (${age} ago)` : `Stale (${age} ago)`;
  const tone = offline
    ? 'bg-[hsl(var(--alarm-major))]/15 text-[hsl(var(--alarm-major))] border-[hsl(var(--alarm-major))]/30'
    : 'bg-muted text-muted-foreground border-border';

  return (
    <span
      data-testid={offline ? 'widget-offline-badge' : 'widget-stale-badge'}
      className={cn(
        'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium',
        tone,
      )}
      title={`Last fresh ${staleSince.toLocaleString()}`}
      aria-label={label}
    >
      <Icon className="h-2.5 w-2.5" aria-hidden />
      {label}
    </span>
  );
};

// ────────────────────────────────────────────────────────────────────────
// WidgetStateView — renders the correct view for a WidgetDataState.
// ────────────────────────────────────────────────────────────────────────

export interface WidgetStateViewProps<T> {
  state: WidgetDataState<T>;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  onRetry?: () => void;
  /** Render function for the ready state. */
  children: (data: T, source: WidgetDataSourceFlavor) => React.ReactNode;
}

export function WidgetStateView<T>({
  state,
  emptyTitle = 'No data',
  emptyDescription = 'There is nothing to show here yet.',
  emptyAction,
  onRetry,
  children,
}: WidgetStateViewProps<T>): React.ReactElement {
  if (state.status === 'loading') {
    return (
      <div
        data-testid="widget-state-loading"
        className="flex h-full items-center justify-center"
      >
        <LoadingSpinner />
      </div>
    );
  }
  if (state.status === 'permission-denied') {
    return (
      <div data-testid="widget-state-permission-denied" className="flex h-full items-center justify-center p-3">
        <ViewState permissionDenied>
          {/* unreachable but satisfies the required children prop */}
          <span />
        </ViewState>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div data-testid="widget-state-error" className="flex h-full items-center justify-center p-3">
        <ViewState error={{ message: state.error.message, retry: onRetry }}>
          <span />
        </ViewState>
      </div>
    );
  }
  if (state.status === 'empty') {
    return (
      <div data-testid="widget-state-empty" className="flex h-full items-center justify-center p-3">
        <ViewState empty emptyTitle={emptyTitle} emptyDescription={emptyDescription} emptyAction={emptyAction}>
          <span />
        </ViewState>
      </div>
    );
  }
  return (
    <div data-testid="widget-state-ready" className="h-full">
      {children(state.data, state.source)}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// WidgetCard — the consistent shell every widget wraps its body with.
// ────────────────────────────────────────────────────────────────────────

export interface WidgetCardEvidence {
  /** Stable id used in the bundle. */
  recordId: string;
  /** Label rendered into the bundle file name. */
  recordLabel: string;
  /** Section resolver. */
  sections: EvidenceSection[] | (() => Promise<EvidenceSection[]>);
}

export interface WidgetCardProps {
  /** The widget descriptor — the card pulls label + moduleId + drilldown + evidence tags. */
  descriptor: Pick<
    WidgetDescriptor,
    'widgetId' | 'label' | 'moduleId' | 'drilldownRoute' | 'evidenceTags'
  >;
  /** Source flavor for the state badge (only shown when set). */
  source?: WidgetDataSourceFlavor;
  /**
   * Last-fresh timestamp. When set (and past the freshness window the caller
   * decided on), the card renders a distinct stale/offline badge with a live
   * 1Hz age delta — separate from the `source` badge. Pass `null`/`undefined`
   * while data is fresh; pass the last-good timestamp once it's stale.
   */
  staleSince?: StaleSince;
  /**
   * True when the live connection backing this widget is down. Surfaces the
   * stale badge as "Offline" (warning tone) rather than the neutral "Stale" so
   * a dropped feed reads differently from merely aged data. Only meaningful
   * alongside `staleSince`.
   */
  offline?: boolean;
  /**
   * What the card does once `staleSince` indicates stale data. `'badge'`
   * (default) keeps the last-known body and shows the badge; `'show-empty'`
   * hides the body and renders the empty state instead. Ignored when fresh.
   */
  staleBehavior?: WidgetStaleBehavior;
  /** Title shown by the `show-empty` stale state. */
  staleEmptyTitle?: string;
  /** Description shown by the `show-empty` stale state. */
  staleEmptyDescription?: string;
  /** Extra actions for the kebab menu. Retry is wired automatically when `onRefresh` is set. */
  actions?: WidgetCardAction[];
  /** Wire to your `useWidgetData` refetch. Renders a refresh entry in the menu. */
  onRefresh?: () => void;
  /** Drilldown handler — if unset, the chevron renders an anchor to `descriptor.drilldownRoute.path`. */
  onDrilldown?: () => void;
  /** Optional evidence bundle — when set, an EvidenceExportButton is rendered in the footer. */
  evidence?: WidgetCardEvidence;
  /** Optional icon left of the title. */
  icon?: React.ReactNode;
  /** Body slot. */
  children: React.ReactNode;
  /** Extra className appended to the root Card. */
  className?: string;
  /** Optional footer slot (rendered below the body). */
  footer?: React.ReactNode;
  /**
   * data-testid override. Defaults to `widget-card-${widgetId}`. Lets the
   * E2E suite anchor on a known id even when multiple widgets share a label.
   */
  testId?: string;
}

const MODULE_BADGE_LABEL: Record<string, string> = {
  'tyndo.admin': 'Admin',
  'tyndo.scada': 'SCADA',
  'tyndo.alarms': 'Alarms',
  'tyndo.quality': 'Quality',
  'tyndo.batch': 'Batch',
  'tyndo.production': 'Production',
  'tyndo.maintenance': 'Maintenance',
  'tyndo.materials': 'Materials',
  'tyndo.inventory': 'Inventory',
  'tyndo.procurement': 'Procurement',
  'tyndo.crm': 'CRM',
  'tyndo.sales': 'Sales',
  'tyndo.finance': 'Finance',
  'tyndo.hr': 'HR',
  'tyndo.plm': 'PLM',
  'tyndo.compliance': 'Compliance',
  'tyndo.copilot': 'Copilot',
  'tyndo.costing': 'Costing',
  'tyndo.designer': 'Designer',
  'tyndo.equipment': 'Equipment',
  'tyndo.predictive': 'Predictive',
  'tyndo.workflow': 'Workflow',
  'tyndo.marketplace': 'Marketplace',
  'tyndo.analytics': 'Analytics',
  'tyndo.integration': 'Integration',
  'tyndo.vision': 'Vision',
  'tyndo.multisite': 'Multi-site',
  'tyndo.sustainability': 'Sustainability',
  'tyndo.digital-twin': 'Digital Twin',
  'tyndo.planning': 'Planning',
  'tyndo.simulator': 'Simulator',
};

function moduleBadge(moduleId: string | undefined): string {
  if (!moduleId) return 'Module';
  if (MODULE_BADGE_LABEL[moduleId]) return MODULE_BADGE_LABEL[moduleId];
  const trimmed = moduleId.replace(/^tyndo\./, '');
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export const WidgetCard: React.FC<WidgetCardProps> = ({
  descriptor,
  source,
  staleSince,
  offline,
  staleBehavior = 'badge',
  staleEmptyTitle = 'Data unavailable',
  staleEmptyDescription = 'The latest data is too old to display.',
  actions,
  onRefresh,
  onDrilldown,
  evidence,
  icon,
  children,
  className,
  footer,
  testId,
}) => {
  const menuItems: WidgetCardAction[] = React.useMemo(
    () => resolveWidgetCardMenu(actions, onRefresh),
    [actions, onRefresh]
  );

  // A valid staleSince timestamp means the caller has decided the data can no
  // longer be trusted. `show-empty` then suppresses the (stale) body; `badge`
  // keeps it but flags it.
  const staleDate = toStaleDate(staleSince);
  const isStale = staleDate !== null;
  const hideBody = isStale && staleBehavior === 'show-empty';

  const drilldownPath = descriptor.drilldownRoute?.path;
  const handleDrilldown = onDrilldown
    ?? (drilldownPath
      ? () => {
          if (typeof window !== 'undefined') window.location.assign(drilldownPath);
        }
      : undefined);

  return (
    <Card
      data-testid={testId ?? `widget-card-${descriptor.widgetId}`}
      data-widget-id={descriptor.widgetId}
      data-module-id={descriptor.moduleId}
      className={cn('flex h-full flex-col', className)}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border/40 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {icon ? <span className="text-muted-foreground">{icon}</span> : null}
          <span
            data-testid={`widget-title-${descriptor.widgetId}`}
            className="truncate text-sm font-semibold"
            title={descriptor.label}
          >
            {descriptor.label}
          </span>
          <Badge variant="outline" className="text-[10px]">
            {moduleBadge(descriptor.moduleId)}
          </Badge>
          {/* Source badge ("Live"/"Cached"/…) and the distinct stale/offline
              badge are independent: a Cached source can still be fresh, and a
              Live source can go stale/offline. Show the stale badge whenever a
              staleSince is supplied, regardless of staleBehavior. */}
          {source ? <WidgetSourceBadge source={source} /> : null}
          {staleDate ? <StaleBadge staleSince={staleDate} offline={offline} /> : null}
        </div>
        <div className="flex items-center gap-1">
          {handleDrilldown ? (
            <Button
              data-testid={`widget-drilldown-${descriptor.widgetId}`}
              variant="ghost"
              size="sm"
              aria-label={`Open ${descriptor.label}`}
              onClick={handleDrilldown}
              className="h-7 w-7 p-0"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          ) : null}
          {menuItems.length > 0 ? (
            <TooltipProvider delayDuration={150}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid={`widget-menu-${descriptor.widgetId}`}
                  variant="ghost"
                  size="sm"
                  aria-label="Widget actions"
                  className="h-7 w-7 p-0"
                >
                  <MoreHorizontal className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {menuItems.map((it) => {
                  const item = (
                    <DropdownMenuItem
                      key={it.id}
                      // Guard select even if a consumer styles around `disabled`:
                      // a disabled action must never fire its handler.
                      onSelect={(e) => {
                        if (it.disabled) {
                          e.preventDefault();
                          return;
                        }
                        it.onSelect();
                      }}
                      disabled={it.disabled}
                      aria-disabled={it.disabled || undefined}
                      title={it.disabled ? it.disabledReason : undefined}
                      data-testid={`widget-menu-item-${descriptor.widgetId}-${it.id}`}
                    >
                      {it.id === 'refresh' ? (
                        <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      ) : null}
                      {it.label}
                    </DropdownMenuItem>
                  );
                  // When a disabled action carries a reason, surface it as a
                  // tooltip so the operator sees *why* it's greyed (Radix items
                  // don't fire hover events while disabled, so wrap a span).
                  if (it.disabled && it.disabledReason) {
                    return (
                      <Tooltip key={it.id}>
                        <TooltipTrigger asChild>
                          <span
                            className="block"
                            data-testid={`widget-menu-item-reason-${descriptor.widgetId}-${it.id}`}
                          >
                            {item}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left">{it.disabledReason}</TooltipContent>
                      </Tooltip>
                    );
                  }
                  return item;
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            </TooltipProvider>
          ) : null}
        </div>
      </header>
      <CardContent className="flex-1 overflow-auto p-3">
        {hideBody ? (
          <div
            data-testid={`widget-stale-empty-${descriptor.widgetId}`}
            className="flex h-full items-center justify-center p-3"
          >
            <ViewState empty emptyTitle={staleEmptyTitle} emptyDescription={staleEmptyDescription}>
              <span />
            </ViewState>
          </div>
        ) : (
          children
        )}
      </CardContent>
      {(evidence || footer) && (
        <footer className="flex items-center justify-between gap-2 border-t border-border/40 px-3 py-2">
          <div className="flex-1">{footer}</div>
          {evidence ? (
            <EvidenceExportButton
              manifest={{
                recordId: evidence.recordId,
                recordLabel: evidence.recordLabel,
                kind: `widget-${descriptor.widgetId}`,
              }}
              sections={evidence.sections}
              label="Export"
            />
          ) : null}
        </footer>
      )}
    </Card>
  );
};

WidgetCard.displayName = 'WidgetCard';
